import { useMemo, useState } from 'react';
import type { BootstrapData, Product } from '../types';
import ProductCard from './ProductCard';
import ProductModal from './ProductModal';
import { APP_VERSION } from '../version';
import useBodyScrollLock from '../hooks/useBodyScrollLock';

export default function PublicStore({ data, onAdmin, theme, onTheme }: { data: BootstrapData; onAdmin: () => void; theme: string; onTheme: () => void }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [condition, setCondition] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  useBodyScrollLock(menuOpen);
  const [selected, setSelected] = useState<Product | null>(() => {
    const id = new URLSearchParams(location.search).get('product');
    return data.products.find((product) => product.id === id) || null;
  });

  const categoryMap = useMemo(() => new Map(data.categories.map((item) => [item.id, item.name])), [data.categories]);
  const children = (id: string) => data.categories.filter((item) => item.parent_id === id);
  const filtered = useMemo(() => data.products.filter((product) => {
    const needle = search.trim().toLowerCase();
    const categoryIds = category ? [category, ...data.categories.filter((item) => item.parent_id === category).map((item) => item.id)] : [];
    return (!needle || `${product.title} ${product.description}`.toLowerCase().includes(needle))
      && (!category || categoryIds.includes(product.category_id))
      && (!condition || product.condition === condition)
      && (!minPrice || product.price_cents >= Number(minPrice) * 100)
      && (!maxPrice || product.price_cents <= Number(maxPrice) * 100);
  }), [data, search, category, condition, minPrice, maxPrice]);

  const openProduct = (product: Product) => {
    setSelected(product);
    const url = new URL(location.href);
    url.searchParams.set('product', product.id);
    history.replaceState(null, '', url);
  };
  const closeProduct = () => {
    setSelected(null);
    const url = new URL(location.href);
    url.searchParams.delete('product');
    history.replaceState(null, '', url);
  };
  const productUrl = (product: Product) => {
    const url = new URL(location.href);
    url.searchParams.set('product', product.id);
    url.hash = '';
    return url.toString();
  };
  const order = (product: Product) => {
    const message = `שלום, אני מעוניין/ת במוצר: ${product.title}\nמחיר: ${(product.price_cents / 100).toLocaleString('he-IL')} ₪\n${productUrl(product)}`;
    window.open(`https://wa.me/${data.settings.whatsapp_phone}?text=${encodeURIComponent(message)}`, '_blank', 'noopener');
  };
  const share = async (product: Product) => {
    const shareData = { title: product.title, text: `${product.title} בחנות יד 2 של נטלי`, url: productUrl(product) };
    try {
      if (navigator.share) await navigator.share(shareData);
      else { await navigator.clipboard.writeText(shareData.url); alert('הקישור הועתק'); }
    } catch { /* User cancelled sharing. */ }
  };

  const roots = data.categories.filter((item) => !item.parent_id);
  return <div className="store-layout">
    <aside className={`sidebar ${menuOpen ? 'open' : ''}`}>
      <button className="mobile-close" onClick={() => setMenuOpen(false)}>×</button>
      <div className="brand"><div className="brand-heart">♥</div><strong>{data.settings.site_title}</strong><span>{data.settings.tagline}</span></div>
      <nav>
        <button className={!category ? 'active' : ''} onClick={() => { setCategory(''); setMenuOpen(false); }}>ראשי <small>{data.products.length}</small></button>
        {roots.map((root) => <div className="nav-group" key={root.id}><button className={category === root.id ? 'active' : ''} onClick={() => { setCategory(root.id); setMenuOpen(false); }}>{root.name}</button>{children(root.id).map((child) => <button className={`sub ${category === child.id ? 'active' : ''}`} key={child.id} onClick={() => { setCategory(child.id); setMenuOpen(false); }}>{child.name}</button>)}</div>)}
      </nav>
      <div className="sidebar-footer"><span className="app-version">גרסה {APP_VERSION}</span><button className="admin-entry" onClick={onAdmin}>כניסה לניהול</button></div>
    </aside>
    {menuOpen && <button className="menu-scrim" onClick={() => setMenuOpen(false)} aria-label="סגירת תפריט" />}
    <main className="store-main">
      <header className="store-header"><button className="mobile-menu" onClick={() => setMenuOpen(true)} aria-label="פתיחת תפריט">☰</button><div><h1>הפריטים החדשים שלנו</h1><p>אולי בדיוק מה שחיפשת מחכה כאן</p></div><button className="theme-button" onClick={onTheme} title={`מצב תצוגה: ${theme}`}>◐</button></header>
      <section className="filters" aria-label="חיפוש וסינון">
        <label className="search-field"><span>⌕</span><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="חיפוש לפי שם או תיאור..." /></label>
        <select value={category} onChange={(event) => setCategory(event.target.value)}><option value="">כל הקטגוריות</option>{data.categories.map((item) => <option key={item.id} value={item.id}>{item.parent_id ? `↳ ${item.name}` : item.name}</option>)}</select>
        <select value={condition} onChange={(event) => setCondition(event.target.value)}><option value="">כל המצבים</option><option value="new">חדש</option><option value="like_new">משומש כמו חדש</option><option value="needs_cleaning">נדרש ניקוי</option></select>
        <div className="price-filter"><input inputMode="numeric" value={minPrice} onChange={(event) => setMinPrice(event.target.value.replace(/\D/g, ''))} placeholder="מחיר מ-" /><input inputMode="numeric" value={maxPrice} onChange={(event) => setMaxPrice(event.target.value.replace(/\D/g, ''))} placeholder="עד" /></div>
      </section>
      {filtered.length ? <section className="product-grid">{filtered.map((product) => <ProductCard key={product.id} product={product} categoryName={categoryMap.get(product.category_id) || ''} onOpen={openProduct} onOrder={order} onShare={share} />)}</section> : <div className="empty-state"><div>♡</div><h2>לא נמצאו פריטים מתאימים</h2><p>אפשר לשנות את החיפוש או את הסינון.</p></div>}
      <footer>מוצגים {filtered.length} מתוך {data.products.length} פריטים</footer>
    </main>
    {selected && <ProductModal product={selected} settings={data.settings} categoryName={categoryMap.get(selected.category_id) || ''} onClose={closeProduct} onOrder={() => order(selected)} onShare={() => share(selected)} />}
  </div>;
}
